/**
 * Lead → latest call → transcript → drafted notes. All on the agent's machine.
 *
 * The chain is:
 *   newest call WITH a recording
 *     -> existing transcript if one is already saved (free, instant)
 *     -> otherwise decode + Whisper in a worker, and SAVE it so the next
 *        person on this lead gets the free path
 *     -> Gemini Nano via the Prompt API for the notes themselves
 *
 * Nothing here uploads audio. The recording is fetched from our own storage,
 * decoded in the tab, and the resulting text is only ever POSTed back as the
 * transcript we already persist for the transcript panel.
 *
 * WHY A SERVICE AND NOT A HOOK: the notes composer needs this as an imperative
 * "do it now" action behind a button, not as data that fetches on mount —
 * transcription costs real seconds and battery, so it must never run because a
 * panel happened to render.
 */
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { getProxyRecordingUrl } from "@/app/_module/lib/utils";
import { draftCallNotes, callNotesToHtml, type CallNotes, type DraftProgress } from "@/app/_module/services/localAI";
import type { beginLocalAiSession } from "@/app/_module/services/localAI";

export interface LeadCall {
  id: string;
  recordingUrl?: string | null;
  duration?: number | null;
  createdAt?: string;
  direction?: string;
}

export type NotesStage =
  | { stage: "finding-call" }
  | { stage: "existing-transcript" }
  | { stage: "fetching-audio" }
  | { stage: "model"; pct: number }
  | { stage: "transcribing"; processed: number; total: number }
  | { stage: "drafting"; detail?: DraftProgress };

export class NoNotesReason extends Error {
  constructor(
    public readonly reason:
      | "no-call"
      | "no-recording"
      | "too-long"
      | "no-speech"
      | "no-local-ai"
      | "failed",
    message: string,
  ) {
    super(message);
  }
}

/**
 * How much audio this MACHINE can transcribe, decided at run time.
 *
 * The old fixed 30-min / 10-min pair plus a localStorage escape hatch was a
 * developer tool pretending to be a feature: an agent is never going to open
 * DevTools to summarize a long call. The limit has to work itself out.
 *
 * The binding constraint is MEMORY, and it is now measured rather than guessed.
 * A real 44.3-minute call (2026-08-21, 22kHz mono mp3) costs roughly:
 *   decoded at source rate  ~235 MB
 *   resampled to 16k mono   ~170 MB
 *   peak with both live     ~400 MB   (~9 MB per minute of audio)
 *
 * So the cap is "how many minutes fit in the memory this device admits to",
 * with a hard ceiling per backend because TIME matters too: WebGPU runs many
 * times realtime, WASM runs at roughly realtime, and no agent should have a
 * tab pegged for 40 minutes mid-shift.
 */
/**
 * Cost per minute of audio, AFTER slicing (2026-08-21).
 *
 * Before slicing it was ~9 MB/min: a decoded source buffer (~5 MB/min at the
 * 22kHz Twilio writes) PLUS one 16kHz Float32Array covering the entire call
 * (~4 MB/min). Rendering five minutes at a time removes the second term — it is
 * now a flat ~19 MB regardless of length — so only the decode scales.
 *
 * That is what makes a 2-hour ceiling reasonable: ~5 MB/min instead of ~9.
 */
const MB_PER_AUDIO_MINUTE = 5;
const SLICE_OVERHEAD_MB = 20;
/** Fraction of reported RAM we are willing to spend on one transcription. */
const MEMORY_BUDGET_FRACTION = 0.15;
const CEILING_SECONDS = { webgpu: 7200, wasm: 900 } as const;
const FLOOR_SECONDS = 600;

export function clientCapSeconds(device: "webgpu" | "wasm"): number {
  // navigator.deviceMemory is coarse (0.25/0.5/1/2/4/8) and absent on Safari.
  // 4 is the conservative stand-in Chrome itself uses as a lower bound.
  const gb = (typeof navigator !== "undefined" && (navigator as any).deviceMemory) || 4;
  const budgetMb = gb * 1024 * MEMORY_BUDGET_FRACTION - SLICE_OVERHEAD_MB;
  const byMemory = (Math.max(0, budgetMb) / MB_PER_AUDIO_MINUTE) * 60;
  // A weak CPU makes the WASM path unusable long before memory runs out.
  const cores = (typeof navigator !== "undefined" && navigator.hardwareConcurrency) || 4;
  const coreScale = device === "wasm" ? Math.min(1, cores / 8) : 1;
  const cap = Math.min(byMemory * coreScale, CEILING_SECONDS[device]);
  return Math.max(FLOOR_SECONDS, Math.round(cap));
}

/**
 * Escape hatch, same key CallTranscriptPanel already uses:
 *   localStorage.setItem("IC_FORCE_CLIENT_TRANSCRIBE", "1")
 *
 * Lifts the duration caps so a long call can be run in the browser on purpose.
 * This exists to MEASURE what a 40-minute call actually costs on real hardware,
 * not as a setting to leave on: the caps guard against ~330MB peak memory and,
 * on a WASM machine, roughly real-time compute (40 minutes of audio ≈ 40 minutes
 * of pegged CPU). Turn it on, watch one call, then set the real cap from
 * evidence.
 */
function capsBypassed(): boolean {
  try {
    return typeof window !== "undefined"
      && window.localStorage?.getItem("IC_FORCE_CLIENT_TRANSCRIBE") === "1";
  } catch {
    return false; // storage can throw in private mode
  }
}

async function pickDevices(): Promise<Array<"webgpu" | "wasm">> {
  try {
    if ((navigator as any).gpu && (await (navigator as any).gpu.requestAdapter())) {
      return ["webgpu", "wasm"];
    }
  } catch {
    /* no adapter — WASM it is */
  }
  return ["wasm"];
}

/**
 * How much audio is turned into a Float32Array at a time.
 *
 * Rendering a whole call at once is what made long calls expensive: at 16kHz
 * mono a 2-hour recording is ~490MB in ONE array, on top of the decoded source
 * buffer. Rendering 5 minutes at a time keeps that side flat at ~19MB no matter
 * how long the call is.
 */
const SLICE_SECONDS = 300;

/** Decode once. This part cannot be sliced — the Web Audio API needs the whole
 *  file to produce an AudioBuffer — so it stays the memory floor (~5MB/min). */
async function decodeWhole(buf: ArrayBuffer): Promise<AudioBuffer> {
  const AC: typeof AudioContext = window.AudioContext || (window as any).webkitAudioContext;
  const probe = new AC();
  const decoded = await probe.decodeAudioData(buf.slice(0));
  await probe.close();
  return decoded;
}

/** Render ONE window of an already-decoded buffer to 16kHz mono. */
async function renderSlice(
  decoded: AudioBuffer,
  offsetSec: number,
  durationSec: number,
): Promise<Float32Array> {
  const frames = Math.max(1, Math.ceil(durationSec * 16000));
  const offline = new OfflineAudioContext(1, frames, 16000);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  // start(when, offset, duration) is what makes this a slice rather than a
  // re-render of the entire recording.
  src.start(0, offsetSec, durationSec);
  return (await offline.startRendering()).getChannelData(0);
}

/** Unwrap the call-history envelope at either nesting depth. */
export function pickItems(res: any): LeadCall[] {
  const candidates = [
    res?.data?.data?.items,
    res?.data?.items,
    res?.items,
    res?.data?.data,
    res?.data,
    res,
  ];
  for (const c of candidates) if (Array.isArray(c)) return c as LeadCall[];
  return [];
}

/**
 * The agency's carrier names, for snapping mangled brand names back.
 * Failure here is not fatal — no list simply means no correction.
 */
export async function listAgencyCarriers(): Promise<string[]> {
  try {
    const res = await handleApiCall<any>(async () =>
      api.get({ url: getApiUrl(endpoint.resources.getCarriers), auth: true }),
    );
    const rows = pickItems(res) as any[];
    return rows
      .map((r) => r?.name || r?.carrier?.name || r?.carrierName)
      .filter((n): n is string => typeof n === "string" && n.length > 2);
  } catch {
    return [];
  }
}

/**
 * Rough throughput, multiples of realtime, for warning the agent BEFORE they
 * commit their laptop to several minutes of work.
 *
 * base.en on a native CPU measured 43x on this hardware (2026-08-21), but the
 * browser runtimes are far slower than native: WebGPU is the good case and WASM
 * runs near realtime. These are deliberately pessimistic — a warning that
 * under-promises and finishes early is fine; one that under-states the cost is
 * how an agent ends up with a frozen-feeling laptop mid-shift.
 */
const REALTIME_MULTIPLE = { webgpu: 6, wasm: 1.1 } as const;

export interface DeviceEstimate {
  device: "webgpu" | "wasm";
  /** Seconds of wall clock this machine will likely spend. */
  seconds: number;
  /** True when it is slow enough to be worth interrupting the agent over. */
  shouldWarn: boolean;
  /** True when this machine has no GPU acceleration at all. */
  slowDevice: boolean;
}

/** What will this cost the agent, on THIS machine, for THIS much audio? */
export async function estimateTranscription(audioSeconds: number): Promise<DeviceEstimate> {
  const devices = await pickDevices();
  const device = devices[0];
  const seconds = Math.round(audioSeconds / REALTIME_MULTIPLE[device]);
  return {
    device,
    seconds,
    // A minute of waiting is a nuisance; three is something you agree to first.
    shouldWarn: seconds >= 60,
    slowDevice: device === "wasm",
  };
}

/** Every call on this lead that has a recording, newest first. */
export async function listRecordedCalls(leadId: string, limit = 25): Promise<LeadCall[]> {
  const res = await handleApiCall<any>(async () =>
    api.get({
      url: `${getApiUrl(endpoint.communication.getCalls)}?leadId=${encodeURIComponent(leadId)}&page=1&limit=${limit}`,
      auth: true,
    }),
  );
  return pickItems(res).filter((c) => !!c?.recordingUrl);
}

/** Newest call on this lead that actually has a recording attached. */
export async function findLatestRecordedCall(leadId: string): Promise<LeadCall> {
  const res = await handleApiCall<any>(async () =>
    api.get({
      url: `${getApiUrl(endpoint.communication.getCalls)}?leadId=${encodeURIComponent(leadId)}&page=1&limit=25`,
      auth: true,
    }),
  );
  // SHAPE (verified against communication.controller.ts getCallHistory, 2026-08-21):
  //   { statusCode, message, success, data: { items: [...], meta: {...} } }
  // and handleApiCall may or may not unwrap one `data` layer depending on the
  // interceptor, which is why LeadDetailsLayout checks BOTH depths. Copy that
  // rather than invent a shape — an early draft here looked for `data.calls`
  // and would have reported "no calls" on every lead.
  const rows: LeadCall[] = pickItems(res);
  if (!Array.isArray(rows) || rows.length === 0) {
    throw new NoNotesReason("no-call", "No calls on this lead yet.");
  }
  const withAudio = rows.find((c) => !!c?.recordingUrl);
  if (!withAudio) {
    throw new NoNotesReason("no-recording", "No call recording available to summarize.");
  }
  return withAudio;
}

async function getSavedTranscript(callId: string): Promise<string | null> {
  try {
    const t = await handleApiCall<any>(async () =>
      api.get({ url: getApiUrl(endpoint.callTranscripts.get(callId)), auth: true }),
    );
    const data = t?.data ?? t;
    if (data?.status === "completed") {
      const text =
        data.fullText || (data.segments || []).map((s: any) => s.text).join(" ");
      return String(text || "").trim() || null;
    }
  } catch {
    /* no transcript yet — fall through to transcribing */
  }
  return null;
}

async function transcribeInBrowser(
  call: LeadCall,
  onStage: (s: NotesStage) => void,
): Promise<string> {
  const devices = await pickDevices();
  const cap = clientCapSeconds(devices[0]);
  if (!capsBypassed() && (call.duration || 0) > cap) {
    throw new NoNotesReason(
      "too-long",
      `This call is ${Math.round((call.duration || 0) / 60)} minutes — longer than this computer can transcribe here (about ${Math.round(cap / 60)}). Open the transcript panel to run it on the server.`,
    );
  }

  onStage({ stage: "fetching-audio" });
  // Recordings are NOT publicly fetchable. They come back as
  // /api/communication/recordings/RE… behind our auth, so a bare fetch of
  // `recordingUrl` 401s (or trips CORS on the vendor URL). Mirror exactly what
  // CallTranscriptPanel does: proxy to same-origin, then send the Supabase
  // bearer. The same-origin assert is deliberate — never stream a customer
  // recording to a third-party host because a URL looked absolute.
  const proxyUrl = getProxyRecordingUrl(call.recordingUrl as string) || (call.recordingUrl as string);
  const resolved = new URL(proxyUrl, window.location.origin);
  if (resolved.origin !== window.location.origin) {
    throw new NoNotesReason("failed", "Recording is not proxyable.");
  }
  const { getSupabaseSession } = await import("@/app/_module/services/auth");
  const token = (await getSupabaseSession())?.access_token;
  if (!token) throw new NoNotesReason("failed", "Not signed in.");
  const resp = await fetch(resolved.toString(), { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) throw new NoNotesReason("failed", `Recording fetch failed (${resp.status})`);
  const decoded = await decodeWhole(await resp.arrayBuffer());
  const total = decoded.duration;

  // ONE worker for the whole call. The model is cached inside it across
  // messages, so slicing costs nothing extra after the first load — a worker
  // per slice would re-pay the model init every five minutes.
  const worker = new Worker(new URL("../components/transcribe.worker.ts", import.meta.url));
  let model = "whisper-tiny.en";
  const segments: Array<{ start: number; end: number; text: string }> = [];

  try {
    for (let offset = 0; offset < total; offset += SLICE_SECONDS) {
      const len = Math.min(SLICE_SECONDS, total - offset);
      const slice = await renderSlice(decoded, offset, len);
      const sliceStart = offset;

      const part = await new Promise<Array<{ start: number; end: number; text: string }>>(
        (resolve, reject) => {
          worker.onmessage = (e: MessageEvent) => {
            const m = e.data;
            if (m?.type === "model-selected") model = m.model;
            else if (m?.type === "model") onStage({ stage: "model", pct: m.pct });
            else if (m?.type === "progress")
              // Report against the WHOLE call, not the slice, or the bar resets
              // to zero every five minutes.
              onStage({ stage: "transcribing", processed: sliceStart + m.processedSeconds, total });
            else if (m?.type === "done") resolve(m.segments || []);
            else if (m?.type === "error") reject(new NoNotesReason("failed", m.message));
          };
          worker.onerror = (err) =>
            reject(new NoNotesReason("failed", err.message || "Worker failed"));
          // Transferring the buffer hands ownership to the worker, so this
          // slice is not copied and is collectable as soon as it is done.
          worker.postMessage(
            { audio: slice, devices, offsetSeconds: sliceStart },
            [slice.buffer],
          );
        },
      );
      segments.push(...part);
    }

    // Persist it. The agent already paid the CPU cost; saving means the
    // transcript panel and the next teammate get it for free.
    if (segments.length) {
      await handleApiCall(async () =>
        api.post({
          url: getApiUrl(endpoint.callTranscripts.saveClient(call.id)),
          body: { segments, durationSeconds: Math.round(total), model },
          auth: true,
        }),
      ).catch(() => undefined); // a failed save must not lose the notes
    }
    return segments.map((s) => s.text).join(" ").trim();
  } finally {
    worker.terminate();
  }
}

/** How many past calls a history draft will pull in. Beyond this the merged
 *  summary gets vague and Nano's context fills with old material. */
const HISTORY_MAX_CALLS = 10;

/**
 * Audio budget for transcribing calls that have no saved transcript yet.
 *
 * WHY A BUDGET AND NOT "ALL OF THEM": prod has 457,894 recorded calls and 554
 * saved transcripts — 0.12% coverage. So in practice almost every history draft
 * starts from nothing, and "summarize all 12 calls" would mean transcribing an
 * hour of audio on the agent's laptop. We transcribe newest-first until the
 * budget is spent, then summarize what we got and SAY how many we used.
 *
 * GPU machines get a real budget; WASM machines get barely more than one call,
 * because CPU transcription runs at roughly real time (40 min of compute for an
 * hour of audio) and an agent mid-shift cannot afford that.
 */

/**
 * Below this, a "call" is not a conversation.
 *
 * Measured on prod 2026-08-21: of 422,456 recorded calls the MEDIAN is 7
 * seconds and 93.3% are under a minute — rings, voicemail drops and instant
 * hangups. Transcribing those returns silence or a carrier greeting, wastes the
 * budget, and feeds noise into the summary as though it were a sales call.
 *
 * 30s is deliberately generous: p90 is 42s, so this keeps roughly the top
 * decile — the calls where somebody actually talked.
 */
export const MIN_CONVERSATION_SECONDS = 30;

export interface HistoryDraft extends DraftedNotes {
  callsUsed: number;
  callsAvailable: number;
  /** Calls with a recording but no saved transcript — deliberately not
   *  transcribed, because doing 10 recordings in the browser would cook the
   *  agent's laptop. Reported so the UI can say what was left out. */
  callsWithoutTranscript: number;
  /** Dials under MIN_CONVERSATION_SECONDS that were never worth transcribing. */
  callsSkippedAsTooShort: number;
}

export interface DraftedNotes {
  notes: CallNotes;
  html: string;
  call: LeadCall;
  usedExistingTranscript: boolean;
}

/**
 * Full chain. Throws NoNotesReason with a reason the UI can phrase for the
 * agent — "no calls yet" and "your browser can't do this" need very different
 * messages and neither is an error worth a toast that says "failed".
 */
export async function draftNotesFromLatestCall(
  leadId: string,
  onStage: (s: NotesStage) => void,
  /** Gesture-backed session from beginLocalAiSession(), opened in the click. */
  session?: ReturnType<typeof beginLocalAiSession> | null,
  /** Prospect name from the CRM — never inferred from audio. */
  leadName?: string | null,
): Promise<DraftedNotes> {
  onStage({ stage: "finding-call" });
  const call = await findLatestRecordedCall(leadId);

  onStage({ stage: "existing-transcript" });
  let transcript = await getSavedTranscript(call.id);
  const usedExistingTranscript = !!transcript;

  if (!transcript) {
    transcript = await transcribeInBrowser(call, onStage);
  }
  if (!transcript) {
    throw new NoNotesReason("no-speech", "No speech was detected in that recording.");
  }

  const notes = await draftCallNotes(transcript, {
    onProgress: (detail) => onStage({ stage: "drafting", detail }),
    session,
    leadName,
    carriers: await listAgencyCarriers(),
  });
  if (!notes) {
    throw new NoNotesReason(
      "no-local-ai",
      "On-device AI isn't available in this browser. Chrome 138+ on desktop supports it.",
    );
  }

  return { notes, html: callNotesToHtml(notes), call, usedExistingTranscript };
}

/**
 * Draft from the WHOLE call history rather than one call.
 *
 * Deliberately uses only transcripts that are ALREADY saved. Transcribing ten
 * recordings locally would take many minutes of pegged CPU, and the value of
 * call #9 is low. If nothing is transcribed yet we transcribe just the newest
 * one so the button still does something useful instead of erroring.
 */
export async function draftNotesFromCallHistory(
  leadId: string,
  onStage: (s: NotesStage) => void,
  /** Gesture-backed session from beginLocalAiSession(), opened in the click. */
  session?: ReturnType<typeof beginLocalAiSession> | null,
  /** Prospect name from the CRM — never inferred from audio. */
  leadName?: string | null,
): Promise<HistoryDraft> {
  onStage({ stage: "finding-call" });
  const allCalls = await listRecordedCalls(leadId, HISTORY_MAX_CALLS);
  if (allCalls.length === 0) {
    throw new NoNotesReason("no-recording", "No call recordings on this lead yet.");
  }
  // Drop the dials that never became conversations. If EVERY call is short we
  // keep them rather than refuse — a 20-second connect is still better than
  // telling the agent there is nothing here.
  const conversations = allCalls.filter((c) => (c.duration || 0) >= MIN_CONVERSATION_SECONDS);
  const calls = conversations.length > 0 ? conversations : allCalls;

  onStage({ stage: "existing-transcript" });
  const pieces: string[] = [];
  let used = 0;
  let missing = 0;
  for (const call of calls) {
    const t = await getSavedTranscript(call.id);
    if (t) {
      const when = call.createdAt ? new Date(call.createdAt).toLocaleDateString() : "earlier";
      // Label each block — without a date the model blends three calls into one
      // event and invents a timeline.
      pieces.push(`--- Call on ${when} ---\n${t}`);
      used += 1;
    } else {
      missing += 1;
    }
  }

  // Fill the gaps, newest first, until the audio budget is spent. This is what
  // makes "all calls" mean something on a lead where nothing was transcribed
  // before — which, at 0.12% coverage, is nearly every lead.
  if (missing > 0) {
    const devices = await pickDevices();
    // Bypassed: one very large budget rather than Infinity, so a lead with 117
    // recordings still terminates instead of running until the tab dies.
    // History gets a slice of the same machine budget — several calls, not one
    // long one, so two-thirds of the single-call cap.
    let budget = capsBypassed() ? 6 * 3600 : Math.round(clientCapSeconds(devices[0]) * 0.66);
    for (const call of calls) {
      if (budget <= 0) break;
      // already have this one
      if (await getSavedTranscript(call.id)) continue;
      const dur = call.duration || 0;
      if (dur <= 0 || dur > budget) continue; // skip rather than blow the budget
      try {
        const text = await transcribeInBrowser(call, onStage);
        if (text) {
          const when = call.createdAt ? new Date(call.createdAt).toLocaleDateString() : "earlier";
          pieces.push(`--- Call on ${when} ---\n${text}`);
          used += 1;
          missing -= 1;
          budget -= dur;
        }
      } catch {
        // One unreadable recording must not kill the whole draft.
      }
    }
  }

  if (pieces.length === 0) {
    throw new NoNotesReason("no-speech", "Couldn't get a transcript from any of these calls.");
  }

  // Oldest first so the summary reads as a story rather than in reverse.
  const transcript = pieces.reverse().join("\n\n");

  const notes = await draftCallNotes(transcript, {
    onProgress: (detail) => onStage({ stage: "drafting", detail }),
    session,
    leadName,
    carriers: await listAgencyCarriers(),
  });
  if (!notes) {
    throw new NoNotesReason(
      "no-local-ai",
      "On-device AI isn't available in this browser. Chrome 138+ on desktop supports it.",
    );
  }

  return {
    notes,
    html: callNotesToHtml(notes, { callsUsed: used }),
    call: calls[0],
    usedExistingTranscript: missing < calls.length,
    callsUsed: used,
    // Report against the CONVERSATIONS considered, not every dial — "used 2 of
    // 3" is the truth an agent can act on; "used 2 of 12" implies we ignored ten
    // real calls when ten of them were 7-second rings.
    callsAvailable: calls.length,
    callsSkippedAsTooShort: allCalls.length - calls.length,
    callsWithoutTranscript: missing,
  };
}
